import { Injectable } from '@angular/core';
import {
    CreatePremiumOrderInput,
    ModelPremiumOrderFilterInput,
    ModelSortDirection,
    PremiumOrder,
    UpdatePremiumOrderInput
} from '@app/core/services/api/API.service';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import { Observable, catchError, finalize, from, tap, throwError } from 'rxjs';

export type PremiumOrderFmsSync = {
    id: string;
    syncWithFms?: string | null;
    fmsInvoiceId?: string | null;
};

const GET_PREMIUM_ORDER_FMS_SYNC = /* GraphQL */ `
    query GetPremiumOrderFmsSync($id: ID!) {
        getPremiumOrder(id: $id) {
            id
            syncWithFms
            fmsInvoiceId
        }
    }
`;

const FMS_SYNC_BATCH_SIZE = 25;

@Injectable({
    providedIn: 'root'
})
export class PremiumOrderService {
    private amplifyClient = generateClient();

    constructor() {}

    async listPremiumOrders(): Promise<PremiumOrder[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listPremiumOrders
                })
            ).data.listPremiumOrders.items;
            if (data) return data as PremiumOrder[];
        } catch (error) {
            console.error('Error finding PremiumOrder:', error);
            return null;
        }
    }
    async premiumOrdersByBuyer(
        orderCreatorId: string
    ): Promise<PremiumOrder[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.PremiumOrdersByBuyer,
                    variables: {
                        orderCreatorId
                    }
                })
            ).data.PremiumOrdersByBuyer.items;
            if (data) return data as PremiumOrder[];
        } catch (error) {
            console.error('Error finding PremiumOrder:', error);
            return null;
        }
    }

    async premiumOrdersByClient(
        premiumOrderClientId: string,
        filter?: ModelPremiumOrderFilterInput | null
    ): Promise<PremiumOrder[]> {
        let data = [];
        let response = null;
        try {
            do {
                response = (
                    await this.amplifyClient.graphql({
                        query: queries.PremiumOrdersByClient,
                        variables: {
                            premiumOrderClientId,
                            filter,
                            nextToken: response?.nextToken ?? null
                        }
                    })
                ).data.PremiumOrdersByClient;
                if (response && response.items) {
                    data.push(...(response.items as PremiumOrder[]));
                }
            } while (response.nextToken !== null);
            if (data) return data as PremiumOrder[];
        } catch (error) {
            console.error('Error finding PremiumOrders:', error);
            return null;
        }
    }

    async premiumOrdersByOrderId(
        active = 'active',
        limit = 1,
        sortDirection = ModelSortDirection.DESC
    ) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: queries.PremiumOrdersByOrderId,
                    variables: {
                        active,
                        sortDirection,
                        limit
                    }
                })
            ).data.PremiumOrdersByOrderId.items;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrder(updateInput: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: updateInput
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrderListsProducts({
        id,
        listsProducts
    }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            listsProducts
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrderStatus({ id, active }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            active
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrderSentToCoupa({
        id,
        sentToCoupa
    }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            sentToCoupa
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updateRequirement({
        id,
        requirement,
        created
    }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            requirement,
                            created
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrderFiles({ id, files }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            files
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrderBuyerInfo({
        id,
        buyerInfo,
        buyersInfo
    }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            buyerInfo,
                            buyersInfo
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrderComments({
        id,
        comments
    }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            comments
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async createPremiumOrder(body: CreatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.createPremiumOrder,
                    variables: {
                        input: body
                    }
                })
            ).data.createPremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrderSteps({
        id,
        currentProcess,
        currentSubprocess,
        processes
    }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            currentProcess,
                            currentSubprocess,
                            processes
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async getPremiumOrderFmsSync(
        id: string
    ): Promise<PremiumOrderFmsSync | null> {
        if (!id) {
            return null;
        }
        try {
            const response = (await this.amplifyClient.graphql({
                query: GET_PREMIUM_ORDER_FMS_SYNC,
                variables: { id }
            })) as { data?: { getPremiumOrder?: PremiumOrderFmsSync | null } };
            const data = response.data?.getPremiumOrder;
            if (data) {
                return data;
            }
        } catch (error) {
            console.error('Error finding PremiumOrder FMS sync:', error);
        }
        return null;
    }

    async getPremiumOrdersFmsSyncByIds(
        ids: string[]
    ): Promise<Map<string, PremiumOrderFmsSync>> {
        const result = new Map<string, PremiumOrderFmsSync>();
        const uniqueIds = [...new Set(ids.filter(Boolean))];
        if (!uniqueIds.length) {
            return result;
        }

        for (let i = 0; i < uniqueIds.length; i += FMS_SYNC_BATCH_SIZE) {
            const chunk = uniqueIds.slice(i, i + FMS_SYNC_BATCH_SIZE);
            const variableDefs = chunk
                .map((_, index) => `$id${index}: ID!`)
                .join(', ');
            const selections = chunk
                .map(
                    (_, index) =>
                        `o${index}: getPremiumOrder(id: $id${index}) { id syncWithFms fmsInvoiceId }`
                )
                .join('\n');
            const query = /* GraphQL */ `
                query GetPremiumOrdersFmsSync(${variableDefs}) {
                    ${selections}
                }
            `;
            const variables = Object.fromEntries(
                chunk.map((id, index) => [`id${index}`, id])
            );

            try {
                const data = ((await this.amplifyClient.graphql({
                    query,
                    variables
                })) as {
                    data: Record<string, PremiumOrderFmsSync | null>;
                }).data;
                Object.values(data ?? {}).forEach(item => {
                    if (item?.id) {
                        result.set(item.id, item);
                    }
                });
            } catch (error) {
                console.error(
                    'Error finding PremiumOrder FMS sync batch:',
                    error
                );
            }
        }

        return result;
    }
}
